import { useEffect } from "react";

/**
 * Window event used to broadcast "deselect everything in the canvas
 * explorer" from any surface (the explorer pane background, the canvas
 * pane click handler, future toolbar shortcuts).
 *
 * Decoupling via a window event keeps the canvas page from having to
 * import explorer state — the canvas page just dispatches; whichever
 * scope is mounted handles its own selection clear.
 */
const EVENT_NAME = "canvas-explorer:deselect";

export function dispatchClearExplorerSelection(): void {
  window.dispatchEvent(new Event(EVENT_NAME));
}

export function useClearExplorerSelectionListener(handler: () => void): void {
  useEffect(() => {
    window.addEventListener(EVENT_NAME, handler);
    return () => window.removeEventListener(EVENT_NAME, handler);
  }, [handler]);
}

/**
 * Cross-scope coordination: each selectable region inside the
 * explorer pane (Canvas Contents, Chats / Agent Tasks, …) owns its
 * own selection set. Clicking a row in one region should clear the
 * others so the user always has one mental "selected" cluster, but
 * the regions deliberately stay split — multi-select shift-range
 * across them isn't meaningful, and the downstream consumers of each
 * set (canvas auto-attach, delete confirmation copy, etc.) want
 * disjoint inputs.
 *
 * The mechanic mirrors `dispatchClearExplorerSelection`: when a
 * region records a new selection it broadcasts its own `scope` id,
 * and the other regions' listeners check `detail.scope !== own` to
 * decide whether to clear themselves.
 */
export type ExplorerSelectionScope = "contents" | "chats";

const CROSS_SCOPE_EVENT = "canvas-explorer:cross-scope-select";

export function dispatchCrossScopeSelect(scope: ExplorerSelectionScope): void {
  window.dispatchEvent(
    new CustomEvent(CROSS_SCOPE_EVENT, { detail: { scope } }),
  );
}

export function useCrossScopeSelectListener(
  /** The current region's id; events tagged with this scope are
   *  ignored (the originating region keeps its own selection). */
  selfScope: ExplorerSelectionScope,
  handler: () => void,
): void {
  useEffect(() => {
    const fn = (e: Event) => {
      const scope = (e as CustomEvent).detail?.scope as
        | ExplorerSelectionScope
        | undefined;
      if (scope === selfScope) return;
      handler();
    };
    window.addEventListener(CROSS_SCOPE_EVENT, fn);
    return () => window.removeEventListener(CROSS_SCOPE_EVENT, fn);
  }, [selfScope, handler]);
}

/**
 * `true` when the click target is (or descends from) an explorer row
 * marked with `data-explorer-row`. Used by the pane background click
 * handler to avoid stealing clicks meant for individual rows.
 */
export function targetIsExplorerRow(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  return !!target.closest("[data-explorer-row]");
}
